import { DealerInfo } from '@appTypes/DealerInfo';
import { FmcPrivateIncentive } from '@appTypes/DealsheetTypes';
import { currencyFormat, formatDate, getVehicleStatus } from '@services/utils/HelperFunctions';

export const getTelephoneNumber = (input) => {
  if (!input) {
    return '';
  }

  const value = input.toString().trim().replace(/^\+/, '');

  if (value.match(/[^0-9]/)) {
    return input;
  }

  let country, city, number;

  switch (value.length) {
    case 10: // +1PPP####### -> C (PPP) ###-####
      country = 1;
      city = value.slice(0, 3);
      number = value.slice(3);
      break;

    case 11: // +CPPP####### -> CCC (PP) ###-####
      country = value[0];
      city = value.slice(1, 4);
      number = value.slice(4);
      break;

    case 12: // +CCCPP####### -> CCC (PP) ###-####
      country = value.slice(0, 3);
      city = value.slice(3, 5);
      number = value.slice(5);
      break;

    default:
      return input;
  }

  if (country == 1) {
    country = '';
  }

  number = number.slice(0, 3) + '-' + number.slice(3);

  return (country + ' (' + city + ') ' + number).trim();
};

// TODO: REVIEW reverse order to take
export const getBuyerOrCoBuyerName = (buyerOrCoBuyer) => {
  if (buyerOrCoBuyer?.fullName) {
    return buyerOrCoBuyer?.fullName;
  }

  if (buyerOrCoBuyer?.firstName === null && buyerOrCoBuyer?.lastName !== null) {
    return `${buyerOrCoBuyer?.lastName}`;
  }
  if (buyerOrCoBuyer?.firstName !== null && buyerOrCoBuyer?.lastName === null) {
    return `${buyerOrCoBuyer?.firstName}`;
  }

  return `${buyerOrCoBuyer?.firstName} ${buyerOrCoBuyer?.lastName}`;
};

export const getBuyerInitials = (buyer: { firstName: string; lastName: string }) => {
  if (buyer?.firstName === null && buyer?.lastName !== null) {
    return `${buyer?.lastName?.slice(0, 1).toUpperCase()}`;
  }
  if (buyer?.firstName !== null && buyer?.lastName === null) {
    return `${buyer?.firstName?.slice(0, 1).toUpperCase()}`;
  }
  if (buyer?.firstName === null && buyer?.lastName === null) {
    return ``;
  }
  return `${buyer?.firstName.slice(0, 1).toUpperCase()} ${buyer?.lastName.slice(0, 1).toUpperCase()}`;
};

export const getBuyerAddress = (buyer) => {
  const address = buyer?.address;
  return `${address?.line1}, ${address?.city}, ${address?.state} ${address?.zip}`;
};

export const isDealerOptIn = (dealerId: string): boolean => {
  const dealerInfos = JSON.parse(localStorage.getItem('dealerInfos'));
  const dealersList = dealerInfos ? dealerInfos.filter((e) => e.brandName != 'Off-Brand') : [];

  if (dealersList.length > 0) {
    return dealersList.find((x) => x.dealerId == dealerId)?.isDealerOptIn;
  }

  return false;
};

export const getBPSScores = (s, offerId, offerDealType) => {
  const dealType = `${offerDealType?.toLowerCase()}Deal`;

  // temporary
  if (offerId === '0') {
    return s?.temporaryReplacementDeal && s?.temporaryReplacementDeal[dealType]?.bps;
  }

  // primary
  if (offerId === '1') {
    return s?.primaryReplacementDeal && s?.primaryReplacementDeal[dealType]?.bps;
  }

  // secondary
  if (offerId === '2') {
    return s?.secondaryReplacementDeal && s?.secondaryReplacementDeal[dealType]?.bps;
  }

  // turn
  if (offerId === '99') {
    return s?.turnReplacementDeal && s?.turnReplacementDeal[dealType]?.bps;
  }

  // default to primary for other offerIds
  return s?.primaryReplacementDeal && s?.primaryReplacementDeal[dealType]?.bps;
};

export const getPrimaryReplacementDealMsrp = (s, offerDealType) => {
  const dealType = `${offerDealType?.toLowerCase()}`;

  switch (dealType) {
    case 'lease':
      return s?.primaryReplacementDeal?.leaseDeal?.msrp;
    case 'cash':
      return s?.primaryReplacementDeal?.cashDeal?.msrp;
    case 'finance':
      return s?.primaryReplacementDeal?.financeDeal?.msrp;
    default:
      return s?.primaryReplacementDeal?.leaseDeal?.msrp;
  }
};

export const getSecondaryReplacementDealMsrp = (s, offerDealType) => {
  const dealType = `${offerDealType?.toLowerCase()}`;

  switch (dealType) {
    case 'lease':
      return s?.secondaryReplacementDeal?.leaseDeal?.msrp;
    case 'cash':
      return s?.secondaryReplacementDeal?.cashDeal?.msrp;
    case 'finance':
      return s?.secondaryReplacementDeal?.financeDeal?.msrp;
    default:
      return s?.secondaryReplacementDeal?.leaseDeal?.msrp;
  }
};

export const brandsInLowerCase = (dealerInfos: DealerInfo[]): string[] => {
  const brands = dealerInfos?.map((e) => e.brandName);
  let brandsInLowerCase = brands.map((b) => {
    if (b.length > 4) {
      b = b.substring(0, 4);
    }
    return b.toLowerCase();
  });
  return brandsInLowerCase;
};

export const fmcIconDetails = (fmcPrivateIncentives: FmcPrivateIncentive[] | undefined) => {
  if (!fmcPrivateIncentives?.length) {
    return {
      fordPrivateIncentiveAmount: null,
      fordPrivateIncentiveExpiryDate: null,
    };
  }
  if (fmcPrivateIncentives.length === 1) {
    return {
      fordPrivateIncentiveAmount: fmcPrivateIncentives[0].privateIncentiveAmount,
      fordPrivateIncentiveExpiryDate: fmcPrivateIncentives[0].endDate,
    };
  }
  if (fmcPrivateIncentives.length > 1) {
    return {
      fordPrivateIncentiveAmount: null,
      fordPrivateIncentiveExpiryDate: fmcPrivateIncentives[0].endDate,
    };
  }
};

export const getFmcTooltipText = (
  fordPrivateIncentive: boolean | undefined,
  fmcPrivateIncentives: FmcPrivateIncentive[] | undefined,
) => {
  const { fordPrivateIncentiveAmount, fordPrivateIncentiveExpiryDate } =
    fmcIconDetails(fmcPrivateIncentives);
  if (!fordPrivateIncentive || !fordPrivateIncentiveExpiryDate) {
    return { amount: null, date: null };
  }
  const fmcAmount = fordPrivateIncentiveAmount ? currencyFormat(fordPrivateIncentiveAmount) : null;
  const fmcDate = formatDate(fordPrivateIncentiveExpiryDate);
  return { fmcAmount, fmcDate };
};
export const isLeaseEnabled = (vehicle) => {
  const isNew = vehicle?.isNew || vehicle?.isVehicleNew;
  const vehicleType = getVehicleStatus(isNew, vehicle.isCpo, vehicle.isGhostVehicle);

  switch (vehicleType) {
    case '':
      return isEnabledBasedOnEligibiltyCheck(vehicle, 'lease');
    case 'new':
      return isEnabledBasedOnEligibiltyCheck(vehicle, 'lease');
    case 'cpo':
    case 'po':
      return false;
    case 'sample':
      return isEnabledBasedOnEligibiltyCheck(vehicle, 'lease');
  }
};

export const isFinanceEnabled = (vehicle) => {
  const isNew = vehicle?.isNew || vehicle?.isVehicleNew;
  const vehicleType = getVehicleStatus(isNew, vehicle.isCpo, vehicle.isGhostVehicle);

  switch (vehicleType) {
    case '':
      return isEnabledBasedOnEligibiltyCheck(vehicle, 'finance');
    case 'new':
      return isEnabledBasedOnEligibiltyCheck(vehicle, 'finance');
    case 'cpo':
    case 'po':
      return true;
    case 'sample':
      return isEnabledBasedOnEligibiltyCheck(vehicle, 'finance');
  }
};

export const isCashEnabled = (vehicle) => {
  const isNew = vehicle?.isNew || vehicle?.isVehicleNew;
  const vehicleType = getVehicleStatus(isNew, vehicle.isCpo, vehicle.isGhostVehicle);

  switch (vehicleType) {
    case '':
      return isEnabledBasedOnEligibiltyCheck(vehicle, 'cash');
    case 'new':
      return isEnabledBasedOnEligibiltyCheck(vehicle, 'cash');
    case 'cpo':
    case 'po':
      return true;
    case 'sample':
      return isEnabledBasedOnEligibiltyCheck(vehicle, 'cash');
  }
};

const isEnabledBasedOnEligibiltyCheck = (vehicle, dealType) => {
  switch (dealType) {
    case 'cash':
      return vehicle.isAvailableForReplacementCash;
    case 'finance':
      return vehicle.isAvailableForReplacementFinance;
    case 'lease':
      return vehicle.isAvailableForReplacementLease;
    default:
      return false;
  }
};

export const getOfferedDealTypeCheck = (data, currentDealType) => {
  return data?.offeredDealType != undefined &&
    data?.offeredDealType != null &&
    data?.offeredDealType != ''
    ? data?.offeredDealType.toLowerCase()
    : currentDealType?.toLowerCase();
};
